import * as THREE from '../libs/three.js/build/three.module.js';
import { Action } from './Actions.js';
import { Utils } from './utils.js';
import { EventDispatcher } from './EventDispatcher.js';

export class Annotation extends EventDispatcher {
  constructor(args = {}) {
    super();

    this.scene = null;
    this._title = args.title || 'No Title';
    this._description = args.description || '';
    this.offset = new THREE.Vector3();
    this.uuid = THREE.MathUtils.generateUUID();

    if (!args.position) {
      this.position = null;
    } else if (args.position.x != null) {
      this.position = args.position;
    } else {
      this.position = new THREE.Vector3(...args.position);
    }

    this.cameraPosition =
      args.cameraPosition instanceof Array
        ? new THREE.Vector3().fromArray(args.cameraPosition)
        : args.cameraPosition;
    this.cameraTarget =
      args.cameraTarget instanceof Array
        ? new THREE.Vector3().fromArray(args.cameraTarget)
        : args.cameraTarget;
    this.radius = args.radius;
    this.view = args.view || null;
    this.keepOpen = false;
    this.descriptionVisible = false;
    this.showDescription = true;
    this.actions = args.actions || [];
    this.isHighlighted = false;
    this._visible = true;
    this.__visible = true;
    this._display = true;
    this._expand = false;
    this.collapseThreshold = [args.collapseThreshold, 100].find(
      (e) => e !== undefined
    );

    this.children = [];
    this.parent = null;
    this.boundingBox = new THREE.Box3();
    this._viewer = null;
    this._folder = null;

    this.domElement = $(`
			<div class="annotation" oncontextmenu="return false;">
				<div class="annotation-titlebar">
					<span class="annotation-label"></span>
				</div>
			</div>
		`);

    this.elTitlebar = this.domElement.find('.annotation-titlebar');
    this.elTitle = this.elTitlebar.find('.annotation-label');
    this.elTitle.append(this._title);

    // LMB: fly to view (if camera annotation) and open annotation properties panel.
    // Track mousedown position to distinguish click from drag;
    // use native capture-phase listeners to bypass jQuery UI draggable interference.
    let downPos = null;
    this.elTitle[0].addEventListener('mousedown', (e) => {
      if (e.button === 0) downPos = { x: e.clientX, y: e.clientY };
    }, true);
    this.elTitle[0].addEventListener('mouseup', (e) => {
      if (e.button !== 0 || !downPos) return;
      const dx = e.clientX - downPos.x;
      const dy = e.clientY - downPos.y;
      downPos = null;
      if (Math.sqrt(dx * dx + dy * dy) > 5) return; // was a drag, not a click
      if (this.hasView()) {
        this.moveHere(this.scene.getActiveCamera());
      }
      this.dispatchEvent({ type: 'click', target: this });
      this.dispatchEvent({ type: 'annotation_label_clicked', annotation: this });
    }, true);

    this.actions = this.actions.map((a) => {
      if (a instanceof Action) {
        return a;
      } else {
        return new Action(a);
      }
    });

    for (let action of this.actions) {
      action.pairWith(this);
    }

    let actions = this.actions.filter(
      (a) => a.showIn === undefined || a.showIn.includes('scene')
    );

    for (let action of actions) {
      let elButton = $(
        `<img src="${action.icon}" class="annotation-action-icon">`
      );
      this.elTitlebar.append(elButton);
      elButton.click(() => action.onclick({ annotation: this }));
    }

    this.domElement.on('touchstart', (e) => {
      this.dispatchEvent({ type: 'annotation_label_clicked', annotation: this });
    });

    this.display = false;
    //this.display = true;
  }

  get folder() {
    return this._folder;
  }

  set folder(folder) {
    this._folder = folder;
  }

  get viewer() {
    return this._viewer;
  }

  set viewer(viewer) {
    this._viewer = viewer;
  }

  installHandles(viewer) {
    if (this._handlesInstalled) {
      return;
    }
    this._handlesInstalled = true;

    let annotationStartPos = this.position.clone();
    let annotationStartOffset = this.offset.clone();

    $(this.domElement).draggable({
      distance: 5,
      start: (event, ui) => {
        annotationStartPos = this.position.clone();
        annotationStartOffset = this.offset.clone();
        $(this.domElement)
          .find('.annotation-titlebar')
          .css('pointer-events', 'auto');
      },
      stop: () => {
        $(this.domElement)
          .find('.annotation-titlebar')
          .css('pointer-events', '');
      },
      drag: (event, ui) => {
        let renderAreaWidth = viewer.renderer.getSize(
          new THREE.Vector2()
        ).width;

        let diff = {
          x: ui.originalPosition.left - ui.position.left,
          y: ui.originalPosition.top - ui.position.top,
        };

        let nDiff = {
          x: -(diff.x / renderAreaWidth) * 2,
          y: (diff.y / renderAreaWidth) * 2,
        };

        let camera = viewer.scene.getActiveCamera();
        let oldScreenPos = new THREE.Vector3()
          .addVectors(annotationStartPos, annotationStartOffset)
          .project(camera);

        let newScreenPos = oldScreenPos.clone();
        newScreenPos.x += nDiff.x;
        newScreenPos.y += nDiff.y;

        let newPos = newScreenPos.clone();
        newPos.unproject(camera);

        let newOffset = new THREE.Vector3().subVectors(newPos, this.position);
        this.offset.copy(newOffset);
      },
    });
  }

  get visible() {
    return this._visible;
  }

  set visible(value) {
    if (this._visible === value) {
      return;
    }

    this._visible = value;

    this.traverse((node) => {
      node.display = value;
    });

    this.dispatchEvent({
      type: 'visibility_changed',
      annotation: this,
    });
  }

  get display() {
    return this._display;
  }

  set display(display) {
    if (this._display === display) {
      return;
    }

    this._display = display;

    if (display) {
      // this.domElement.fadeIn(200);
      this.domElement.show();
    } else {
      // this.domElement.fadeOut(200);
      this.domElement.hide();
    }
  }

  get expand() {
    return this._expand;
  }

  set expand(expand) {
    if (this._expand === expand) {
      return;
    }

    if (expand) {
      this.display = false;
    } else {
      this.display = true;
      this.traverseDescendants((node) => {
        node.display = false;
      });
    }

    this._expand = expand;
  }

  get title() {
    return this._title;
  }

  set title(title) {
    if (this._title === title) {
      return;
    }

    this._title = title;
    this.elTitle.empty();
    this.elTitle.append(this._title);

    this.dispatchEvent({
      type: 'annotation_changed',
      annotation: this,
    });
  }

  get description() {
    return this._description;
  }

  set description(description) {
    if (this._description === description) {
      return;
    }

    this._description = description;

    this.dispatchEvent({
      type: 'annotation_changed',
      annotation: this,
    });
  }

  add(annotation) {
    if (!this.children.includes(annotation)) {
      this.children.push(annotation);
      annotation.parent = this;
      let descendants = [];
      annotation.traverse((a) => {
        descendants.push(a);
      });
      for (let descendant of descendants) {
        let c = this;
        while (c !== null) {
          c.dispatchEvent({
            type: 'annotation_added',
            annotation: descendant,
          });
          c = c.parent;
        }
      }
    }
  }

  level() {
    if (this.parent === null) {
      return 0;
    } else {
      return this.parent.level() + 1;
    }
  }

  hasChild(annotation) {
    return this.children.includes(annotation);
  }

  remove(annotation) {
    if (this.hasChild(annotation)) {
      annotation.removeAllChildren();
      annotation.dispose();
      this.children = this.children.filter((e) => e !== annotation);
      annotation.parent = null;
    }
    annotation.dispose();
    annotation.parent = null;
    this.dispatchEvent({
      type: 'annotation_removed',
      scene: this,
      annotation: annotation,
    });
  }

  removeAllChildren() {
    this.children.forEach((child) => {
      if (child.children.length > 0) {
        child.removeAllChildren();
        this.dispatchEvent({
          type: 'annotation_removed',
          scene: this,
          annotation: child,
        });
      }
      this.remove(child);
    });
  }

  updateBounds() {
    let box = new THREE.Box3();

    if (this.position) {
      box.expandByPoint(this.position);
    }

    for (let child of this.children) {
      child.updateBounds();

      box.union(child.boundingBox);
    }

    this.boundingBox.copy(box);
  }

  traverse(handler) {
    let expand = handler(this);

    if (expand === undefined || expand === true) {
      for (let child of this.children) {
        child.traverse(handler);
      }
    }
  }

  traverseDescendants(handler) {
    for (let child of this.children) {
      child.traverse(handler);
    }
  }

  flatten() {
    let annotations = [];

    this.traverse((annotation) => {
      annotations.push(annotation);
    });

    return annotations;
  }

  descendants() {
    let annotations = [];

    this.traverse((annotation) => {
      if (annotation !== this) {
        annotations.push(annotation);
      }
    });

    return annotations;
  }

  setHighlighted(highlighted) {
    if (highlighted) {
      this.domElement.css('opacity', '1');
      this.domElement.css('z-index', '1000');
    } else {
      this.domElement.css('opacity', '0.5');
      this.domElement.css('z-index', '100');
    }

    this.isHighlighted = highlighted;
  }

  hasView() {
    let hasPosTargetView = this.cameraTarget != null;
    hasPosTargetView = hasPosTargetView && this.cameraPosition != null;

    let hasRadiusView = this.radius !== undefined;

    let hasView = hasPosTargetView || hasRadiusView;

    return hasView;
  }

  moveHere(camera) {
    let images360 = this.scene.images360;
    if (images360.length !== 0) {
      for (let i of images360) {
        if (i.getCurrentlyFocused() !== null) {
          i.unfocus();
          break;
        }
      }
    }

    let view = this.scene.view;
    let animationDuration = 500;
    let easing = TWEEN.Easing.Quartic.Out;

    let endTarget;
    if (this.cameraTarget && this.cameraTarget !== null) {
      endTarget = this.cameraTarget;
    } else if (this.position) {
      endTarget = this.position;
    } else {
      endTarget = this.boundingBox.getCenter(new THREE.Vector3());
    }

    if (this.cameraPosition && this.cameraPosition !== null) {
      let endPosition = this.cameraPosition;
      Utils.moveTo(this.scene, endPosition, endTarget);
    } else if (this.radius) {
      let direction = view.direction;
      let endPosition = endTarget
        .clone()
        .add(direction.multiplyScalar(-this.radius));
      let startRadius = view.radius;
      let endRadius = this.radius;

      {
        // animate camera position
        let tween = new TWEEN.Tween(view.position).to(
          endPosition,
          animationDuration
        );
        tween.easing(easing);
        tween.start();
      }

      {
        // animate radius
        let t = { x: 0 };

        let tween = new TWEEN.Tween(t)
          .to({ x: 1 }, animationDuration)
          .onUpdate(function () {
            view.radius = this.x * endRadius + (1 - this.x) * startRadius;
          });
        tween.easing(easing);
        tween.start();
      }
    } else {
      let target = new THREE.Vector3(endTarget.x, endTarget.y, endTarget.z);

      let dir = target.clone().sub(viewer.scene.view.position).normalize();
      let move = dir.multiplyScalar(2);
      let newCamPos = target.clone().sub(move);

      this.scene.view.setView(newCamPos, target, 500);
    }
  }

  dispose() {
    if (this.domElement.parentElement) {
      this.domElement.parentElement.removeChild(this.domElement);
    }
    this.domElement.remove();
  }

  toString() {
    return 'Annotation: ' + this._title;
  }
}
